/**
 * CertificationTab - Shows vessel certification requirements
 * Based on vessel type and operations
 */

import React from 'react';
import { Award, AlertCircle, FileCheck, Info } from 'lucide-react';
import { getVesselRequirements } from '../../../../config/vesselRequirements';

const CertificationTab = ({ vesselConfig, updateConfig }) => {
  const requirements = getVesselRequirements(vesselConfig);
  const certificates = requirements.certificates || [];
  
  // Determine primary certificate
  const primaryCert = certificates.find(cert => cert.replaces === 'standard_workboat_cert') 
    || certificates.find(cert => cert.id === 'standard_workboat_cert')
    || { name: 'Workboat Certificate', code: 'WB', reference: 'WBC3 Section 3' };
  
  // Get additional certificates (don't replace standard cert)
  const additionalCerts = certificates.filter(cert => cert.additional);
  
  // Generate vessel number based on type
  const getVesselNumber = () => {
    const year = new Date().getFullYear().toString().slice(-2);
    const code = primaryCert.code || 'WB';
    const length = Math.floor(parseFloat(vesselConfig.lengthOverall) || 0).toString().padStart(3, '0');
    return `UK-${code}-${year}-${length}-XXXX`;
  };

  return (
    <div className="space-y-6">
      {/* Primary Certificate */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Primary Vessel Certificate</h3>
        <div className={`border rounded-lg p-4 ${
          primaryCert.code === 'PO' ? 'bg-purple-50 border-purple-200' :
          primaryCert.code === 'PB' ? 'bg-blue-50 border-blue-200' :
          'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-start gap-3">
            <Award className={`h-6 w-6 flex-shrink-0 mt-0.5 ${
              primaryCert.code === 'PO' ? 'text-purple-600' :
              primaryCert.code === 'PB' ? 'text-blue-600' :
              'text-gray-600'
            }`} />
            <div className="flex-1">
              <h4 className="text-lg font-medium text-gray-900">{primaryCert.name}</h4>
              {primaryCert.description && (
                <p className="mt-1 text-sm text-gray-600">{primaryCert.description}</p>
              )}
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-700">Certificate Code:</span>
                  <span className="font-mono bg-white px-2 py-0.5 rounded border border-gray-300">
                    {primaryCert.code}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-700">Example Vessel Number:</span>
                  <span className="font-mono bg-white px-2 py-0.5 rounded border border-gray-300">
                    {getVesselNumber()}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-700">Validity Period:</span>
                  <span>{primaryCert.validityPeriod || 5} years</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-700">Reference:</span>
                  <span className="text-blue-600">{primaryCert.reference}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Certificates */}
      {additionalCerts.length > 0 && (
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-4">Additional Certificates Required</h3>
          <div className="space-y-3">
            {additionalCerts.map(cert => (
              <div key={cert.id} className="border border-orange-200 bg-orange-50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FileCheck className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{cert.name}</h4>
                    {cert.description && (
                      <p className="mt-1 text-sm text-gray-600">{cert.description}</p>
                    )}
                    <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
                      <span>Validity: {cert.validityPeriod || 5} years</span>
                      <span>Reference: {cert.reference}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Workboat Pilot Endorsement Option */}
      {vesselConfig.vesselType !== 'pilot' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="hasWorkboatPilotEndorsement"
                  checked={vesselConfig.hasWorkboatPilotEndorsement || false}
                  onChange={(e) => updateConfig({ hasWorkboatPilotEndorsement: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="hasWorkboatPilotEndorsement" className="text-sm font-medium text-gray-900">
                  Workboat Pilot Endorsement
                </label>
              </div>
              <p className="mt-2 ml-7 text-sm text-gray-600">
                Enable this if the vessel operates as a pilot boat but is not a dedicated pilot vessel.
                This adds pilot boat specific requirements such as propeller shaft examination records.
              </p>
              {vesselConfig.hasWorkboatPilotEndorsement && (
                <div className="mt-3 ml-7 text-sm text-blue-800">
                  <p className="font-medium">Additional Requirements:</p>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    <li>Propeller shaft examination records (WBC3 4.10.1)</li>
                    <li>Oil analysis records if oil lubricated shaft (WBC3 4.10.3)</li>
                    <li>Subject to Section 27 pilot boat requirements</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Special Requirements Notice */}
      {(vesselConfig.vesselType === 'police' || vesselConfig.vesselType === 'pilot') && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-2">Special Certification Requirements</p>
              {vesselConfig.vesselType === 'police' && (
                <ul className="list-disc list-inside space-y-1">
                  <li>Must be owned or operated by a Police Service</li>
                  <li>Requires enhanced LSA familiarisation certification for crew</li>
                  <li>Subject to Annex 3 additional requirements</li>
                  <li>3-month renewal window before expiry</li>
                </ul>
              )}
              {vesselConfig.vesselType === 'pilot' && (
                <ul className="list-disc list-inside space-y-1">
                  <li>Can be either dedicated Pilot Boat or Workboat with Pilot Boat Endorsement</li>
                  <li>Requires Stability Information Booklet</li>
                  <li>Subject to Section 27 requirements</li>
                  <li>Non-seagoing pilot boats have specific area requirements</li>
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Certification Process */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Certification Process</h3>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <ol className="space-y-3 text-sm text-gray-700">
            <li className="flex gap-3">
              <span className="font-medium text-gray-900">1.</span>
              <div>
                <span className="font-medium">Initial Survey</span>
                <p className="text-gray-600 mt-0.5">Complete compliance examination in and out of water</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="font-medium text-gray-900">2.</span>
              <div>
                <span className="font-medium">Documentation</span>
                <p className="text-gray-600 mt-0.5">Submit required declarations and records</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="font-medium text-gray-900">3.</span>
              <div>
                <span className="font-medium">Certificate Issue</span>
                <p className="text-gray-600 mt-0.5">Certifying Authority issues certificate (valid {primaryCert.validityPeriod || 5} years)</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="font-medium text-gray-900">4.</span>
              <div>
                <span className="font-medium">Intermediate Examination</span>
                <p className="text-gray-600 mt-0.5">Required within 36 months of issue</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="font-medium text-gray-900">5.</span>
              <div>
                <span className="font-medium">Annual Examinations</span>
                <p className="text-gray-600 mt-0.5">Annual compliance checks required</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="font-medium text-gray-900">6.</span>
              <div>
                <span className="font-medium">Renewal</span>
                <p className="text-gray-600 mt-0.5">Full renewal examination before expiry</p>
              </div>
            </li>
          </ol>
        </div>
      </div>

      {/* Certifying Authorities */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Approved Certifying Authorities</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="mb-2">Certificates can be issued by MCA-approved Certifying Authorities including:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Mecal</li>
                <li>YDSA</li>
                <li>SCMS</li>
                <li>IIMS</li>
                <li>HPR</li>
              </ul>
              <p className="mt-2 text-xs">See MIN 538 for full list of approved authorities</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificationTab;